import React from 'react';
import { Award, Users, Building2, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DITU_PHOTO = 'https://customer-assets.emergentagent.com/job_izi-travels-preview/artifacts/2b3pzpgq_Exc%20DITU.png';

const About = () => {
  const { t } = useLanguage();

  return (
    <section id="apropos" className="py-24 bg-gradient-to-b from-white to-blue-50/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <Award className="text-blue-600" size={18} />
            <span className="text-sm font-semibold text-blue-700">{t('aboutBadge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('aboutTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('aboutSubtitle')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <div className="bg-white p-6 rounded-2xl shadow-md text-center border border-gray-100">
            <Calendar className="mx-auto text-blue-600 mb-2" size={28} />
            <div className="text-3xl font-bold text-gray-900">20+</div>
            <div className="text-sm text-gray-500">{t('yearsExperience')}</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md text-center border border-gray-100">
            <Users className="mx-auto text-blue-600 mb-2" size={28} />
            <div className="text-3xl font-bold text-gray-900">10000+</div>
            <div className="text-sm text-gray-500">{t('clientsServed')}</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md text-center border border-gray-100">
            <Building2 className="mx-auto text-blue-600 mb-2" size={28} />
            <div className="text-3xl font-bold text-gray-900">20+</div>
            <div className="text-sm text-gray-500">{t('destinationsCount')}</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md text-center border border-gray-100">
            <Award className="mx-auto text-blue-600 mb-2" size={28} />
            <div className="text-3xl font-bold text-gray-900">12+</div>
            <div className="text-sm text-gray-500">{t('airlinePartners')}</div>
          </div>
        </div>

        {/* Story + Director */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Story */}
          <div className="lg:col-span-7">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {t('ourStoryTitle')}
            </h3>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              {t('ourStoryP1')}
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('ourStoryP2')}
            </p>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-l-4 border-blue-600 p-5 rounded-r-xl">
              <p className="text-gray-800 italic leading-relaxed">
                "{t('directorQuote')}"
              </p>
            </div>
          </div>

          {/* Director Card */}
          <div className="lg:col-span-5 relative max-w-md w-full mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-blue-200/50 hover:shadow-2xl transition-all duration-500">
              <div className="aspect-[5/4] overflow-hidden bg-gradient-to-br from-blue-100 to-blue-50">
                <img
                  src={DITU_PHOTO}
                  alt="Monsieur DITU MONIZI Blaise"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-2 h-2 bg-blue-600 rounded-full" />
                  <span className="text-xs text-blue-600 font-semibold uppercase tracking-wide">
                    {t('directorLabel')}
                  </span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-1">
                  DITU MONIZI Blaise
                </h4>
                <p className="text-gray-500 mb-3">{t('directorTitle')}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                    {t('honoraryDeputy')}
                  </span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
                    {t('ceniMember')}
                  </span>
                  <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
                    🇨🇩 RD Congo
                  </span>
                </div>
              </div>
            </div>

            {/* Decorative element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-600/10 rounded-full -z-10 blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-400/10 rounded-full -z-10 blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
